import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { OpsDeviceKind, OpsLiveStatusEntry } from '@m8-app/contracts/internal/ops.js';
import { getDevicePlaneLiveStatus } from '../api/devicePlane.js';

const KINDS: OpsDeviceKind[] = ['stair', 'ceiling', 'lift', 'unknown'];
const KIND_LABEL: Record<OpsDeviceKind, string> = {
  stair: 'Stair lights',
  ceiling: 'Ceiling lights',
  lift: 'Lifts',
  unknown: 'Uncategorised',
};

function Chip({ children, tone }: { children: React.ReactNode; tone?: 'ok' | 'warn' | 'mode' | undefined }) {
  const cls =
    tone === 'ok' ? 'bg-good/15 text-goodink'
    : tone === 'warn' ? 'bg-warn/15 text-warnink'
    : tone === 'mode' ? 'bg-accent/15 text-[#a9c9f2]'
    : 'bg-surface2 text-ink2';
  return <span className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function PresenceChip({ presence }: { presence: string }) {
  const cls =
    presence === 'online' ? 'bg-good/15 text-goodink'
    : presence === 'offline' ? 'bg-bad/15 text-badink'
    : 'bg-surface2 text-muted';
  return <span className={`inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 ${cls}`}>{presence}</span>;
}

function LightCard({ d }: { d: OpsLiveStatusEntry }) {
  return (
    <div className="bg-surface border border-white/10 rounded-xl p-4">
      <div className="flex items-baseline justify-between gap-2">
        <Link to={`/device-plane/fleet/${d.deviceId}`} className="font-mono text-sm font-semibold text-ink hover:text-accent">
          {d.serialNumber}
        </Link>
        <PresenceChip presence={d.presence} />
      </div>
      {d.statusTs ? (
        <>
          <div className="h-1.5 rounded-full bg-surface2 mt-3 overflow-hidden">
            <div className="h-full rounded-full bg-warn" style={{ width: `${d.brightness ?? 0}%` }} />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Chip>brightness {d.brightness ?? '—'}%</Chip>
            {d.mode ? <Chip tone="mode">{d.mode}</Chip> : <Chip>no mode</Chip>}
            <Chip>LDR {d.ldrEnabled ? 'on' : 'off'}</Chip>
            <Chip tone="mode">{d.kind}</Chip>
          </div>
          <p className="text-[11px] text-muted mt-2">status {new Date(d.statusTs).toLocaleTimeString()}</p>
        </>
      ) : (
        <p className="text-xs text-muted mt-3">No status snapshot yet — appears after the device&apos;s first status message.</p>
      )}
    </div>
  );
}

function LiftCard({ d }: { d: OpsLiveStatusEntry }) {
  return (
    <div className="bg-surface border border-white/10 rounded-xl p-4">
      <div className="flex items-baseline justify-between gap-2">
        <Link to={`/device-plane/fleet/${d.deviceId}`} className="font-mono text-sm font-semibold text-ink hover:text-accent">
          {d.serialNumber}
        </Link>
        <PresenceChip presence={d.presence} />
      </div>
      {d.statusTs ? (
        <>
          <p className="text-3xl font-bold mt-2 leading-none text-ink">
            {d.currentFloor === null ? '—' : d.currentFloor === 0 ? 'G' : d.currentFloor}
            <span className="text-xs font-normal text-muted ml-2">current floor</span>
          </p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Chip tone={d.doorOpen ? 'warn' : undefined}>door {d.doorOpen ? 'OPEN' : 'closed'}</Chip>
            <Chip>fan {d.fan ? 'on' : 'off'}</Chip>
            <Chip>light {d.light ? 'on' : 'off'}</Chip>
            <Chip tone="mode">lift</Chip>
          </div>
          <p className="text-[11px] text-muted mt-2">status {new Date(d.statusTs).toLocaleTimeString()}</p>
        </>
      ) : (
        <p className="text-xs text-muted mt-3">No status snapshot yet — appears after the device&apos;s first status message.</p>
      )}
    </div>
  );
}

export function DevicePlaneLiveStatus() {
  const [kind, setKind] = useState('');
  const [presence, setPresence] = useState('');
  const [search, setSearch] = useState('');

  const { data } = useQuery({
    queryKey: ['dp-live-status', kind],
    queryFn: () => getDevicePlaneLiveStatus(kind || undefined),
    refetchInterval: 5000,
  });

  const items = (data?.items ?? []).filter((d) =>
    (!presence || d.presence === presence) &&
    (!search || d.serialNumber.toLowerCase().includes(search.toLowerCase())),
  );
  const groups = KINDS
    .map((k) => ({ kind: k, items: items.filter((d) => d.kind === k) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-ink">Device Plane — Live Status</h1>
          <p className="text-sm text-muted mt-0.5">
            Last state snapshot each device reported on its status topic — brightness/mode for lights, floor/door for lifts.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search serial…"
            className="bg-surface border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-accent w-44"
          />
          <select
            value={presence}
            onChange={(e) => setPresence(e.target.value)}
            className="bg-surface border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Presence filter"
          >
            <option value="">All presence</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="unknown">Unknown</option>
          </select>
          <div className="flex gap-1">
            {['', ...KINDS].map((k) => (
              <button
                key={k || 'all'}
                onClick={() => setKind(k)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  kind === k
                    ? 'bg-accent text-white border-accent'
                    : 'bg-surface text-ink2 border-white/10 hover:bg-surface2'
                }`}
              >
                {k === '' ? 'All' : KIND_LABEL[k as OpsDeviceKind]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {groups.length === 0 && (
        <div className="bg-surface border border-white/10 rounded-xl p-8 text-center text-sm text-muted">
          No devices match the filters.
        </div>
      )}

      {groups.map((g) => (
        <div key={g.kind}>
          <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
            {KIND_LABEL[g.kind]} <span className="font-normal">({g.items.length})</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {g.items.map((d) =>
              d.kind === 'lift' ? <LiftCard key={d.deviceId} d={d} /> : <LightCard key={d.deviceId} d={d} />,
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
